import { Link, LinkType } from "~/common/components/Link";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { useLockBodyScroll } from "~/common/hooks/useLockBodyScroll";
import type { ContentDataItem, SrpProductData } from "~/utils/models";
import { useEffect, useMemo, useRef, useState } from "react";
import { createHighlightedText } from "~/features/search/components/Autosuggest/helpers";
import type { AutosuggestProductGroup } from "~/domains/navigation/types/NavigationTypes";
import type { SearchEntry } from "~/features/search/components/Autosuggest/RecentSearches/RecentSearches";
import SearchItem from "~/features/search/components/Autosuggest/SearchRelatedResults/SearchItem";
import { SEARCH_PAGE } from "~/utils/url/staticUrls";
import { getToolConfiguration } from "~/domains/tools/config/ToolConfig";
import { useTranslation } from "react-i18next";
import { saveSearchTerm } from "~/features/search/api/saveSearchTerm";
import { ProductItem } from "~/features/search/components/Autosuggest/SearchRelatedResults/ProductItem/ProductItem";
import { Skeleton, Stack, styled, Typography } from "~/common/mui";
import SearchIcon from "@mui/icons-material/Search";

interface SearchRelatedResultsProps {
  className?: string | false;
  products?: SrpProductData[];
  suggestions?: SearchEntry[];
  groups?: AutosuggestProductGroup[];
  assets?: ContentDataItem[];
  state: "loading" | "loaded" | "error";
  query: string;
  hide: () => void;
}

const MAX_SUGGESTIONS = 4;
const MAX_SUGGESTIONS_TABLET = 3;
const MAX_GROUPS = 3;
const MAX_GROUPS_TABLET = 2;
const MAX_ASSETS = 10;
const MAX_ASSETS_TABLET = 8;
const MAX_PRODUCTS = 5;

enum ContentTypes {
  Suggestions,
  Groups,
  Assets,
}

const SearchRelatedResults = ({
  products,
  suggestions,
  query,
  groups,
  assets,
  state,
  hide,
  className,
}: SearchRelatedResultsProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  if (!isDesktop) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useLockBodyScroll();
  }

  const refList = useRef<HTMLAnchorElement[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const showAllResultsUrl = `${SEARCH_PAGE}?${new URLSearchParams({ text: query }).toString()}`;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex + 1) % refList.current.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex - 1 + refList.current.length) % refList.current.length);
      } else if (event.key === "Escape") {
        event.preventDefault();
        hide();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [refList]);

  useEffect(() => {
    if (focusedIndex !== -1) {
      refList.current[focusedIndex].focus();
    }
  }, [focusedIndex]);

  const configuratorTypes = [
    ...new Set(
      products
        ?.map((product) => product.configuratorTypes)
        .flatMap((toolType) => toolType)
        .map((toolType) => getToolConfiguration(toolType)),
    ),
  ];

  const contentWithHighlights = useMemo(() => {
    const assetsWithConfigurators = assets && [
      ...configuratorTypes.map((config) => ({
        title: config.name,
        label: config.url,
      })),
      ...assets,
    ];

    return {
      suggestions: suggestions?.slice(0, isDesktop ? MAX_SUGGESTIONS : MAX_SUGGESTIONS_TABLET).map((item) => ({
        ...item,
        highlight: createHighlightedText(item.term, query),
        term: item.term,
      })),

      groups: groups?.slice(0, isDesktop ? MAX_GROUPS : MAX_GROUPS_TABLET).map((item) => ({
        ...item,
        highlight: createHighlightedText(item.name ?? "", query),
        term: item.name,
      })),

      assets: assetsWithConfigurators?.slice(0, MAX_ASSETS).map((item) => {
        const translatedTitle = t(item.title);
        return {
          ...item,
          highlight: createHighlightedText(translatedTitle, query),
          term: translatedTitle,
        };
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, groups, assets]);

  const createLinkItem = (
    href: string,
    content: JSX.Element,
    state: "loading" | "loaded" | "error",
    key: number,
    term: string,
    type?: ContentTypes,
  ) => {
    return state === "loaded" ? (
      <SuggestionsLink
        key={key}
        variant={LinkType.SECONDARY}
        type="button"
        onClick={() => saveSearchTerm(term, href)}
        href={href}
        ref={(el) => {
          if (el && !refList.current.includes(el)) {
            refList.current.push(el);
          }
        }}
        {...(type === ContentTypes.Groups && { "tracking-aux-sch-prodgroup-name": term })}
        {...(type === ContentTypes.Assets && { "tracking-aux-sch-content-name": term })}
      >
        {content}
      </SuggestionsLink>
    ) : (
      <Skeleton key={key} variant="rectangular" />
    );
  };

  const dynamicMaxAssets =
    (isDesktop ? MAX_ASSETS : MAX_ASSETS_TABLET) -
    (contentWithHighlights?.suggestions?.length ?? 0) -
    (contentWithHighlights?.groups?.length ?? 0);

  return (
    <SearchRelatedResultContainer className={className || undefined}>
      <Container direction="row">
        <ContentContainer>
          {contentWithHighlights.suggestions && contentWithHighlights.suggestions.length > 0 && (
            <SearchItem
              title={t("searchRelated.suggestions")}
              content={contentWithHighlights.suggestions?.map((suggestion, index) =>
                createLinkItem(suggestion.url, suggestion.highlight, state, index, suggestion.term),
              )}
            />
          )}
          {contentWithHighlights.groups && contentWithHighlights.groups.length > 0 && (
            <SearchItem
              title={t("searchProduct.productGroups")}
              content={contentWithHighlights.groups?.map((group, index) =>
                createLinkItem(group.url, group.highlight, state, index, group.term ?? "", ContentTypes.Groups),
              )}
            />
          )}
          {contentWithHighlights.assets && contentWithHighlights.assets.length > 0 && (
            <SearchItem
              title={t("searchRelated.usefulAssets")}
              content={contentWithHighlights.assets
                ?.slice(0, dynamicMaxAssets)
                .map((asset, index) =>
                  createLinkItem(asset.label, asset.highlight, state, index, asset.term, ContentTypes.Assets),
                )}
            />
          )}
        </ContentContainer>
        {isDesktop && products && products?.length > 0 && (
          <ContentContainer>
            <Header variant="titleLarge">{t("header.autosuggest.col.product")}</Header>
            <ProductContainer data-zoovu={"searchSuggestions"}>
              {products?.slice(0, MAX_PRODUCTS).map((item, index) => {
                if (state === "loading") {
                  return <Skeleton key={index} variant="rectangular" height={64} />;
                }

                return (
                  <ProductItem
                    key={index}
                    ref={(el) => {
                      if (el && !refList.current.includes(el)) {
                        refList.current.push(el);
                      }
                    }}
                    product={item}
                  />
                );
              })}
            </ProductContainer>
          </ContentContainer>
        )}
      </Container>
      <TotalResults>
        <Link
          href={showAllResultsUrl}
          iconBefore={<SearchIcon  />}
          tracking-aux-sch-show-total-results-link="true"
        >
          {t("searchRelated.showAll")}
        </Link>
      </TotalResults>
    </SearchRelatedResultContainer>
  );
};

export default SearchRelatedResults;

const SearchRelatedResultContainer = styled("div")(({ theme }) => ({
  overflow: "auto",
  [theme.breakpoints.down("tablet")]: {
    overflowY: "scroll",
    minHeight: theme.spacing(112.5),
  },
}));

const SuggestionsLink = styled(Link)(({ theme }) => ({
  padding: theme.spacing(0.5, 2),
}));

const Header = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Container = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const ProductContainer = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1.5),
  borderLeft: `1px solid ${theme.palette.divider}`,
  flex: 1,
  ":focus": {
    color: theme.palette.primary.main,
  },
}));

const TotalResults = styled(Typography)(({ theme }) => ({
  background: theme.palette.surfaceContainer.lowest,
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(3, 4.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  ...theme.typography.labelLarge,
  [theme.breakpoints.up("tablet")]: {
    position: "inherit",
    border: "none",
    padding: theme.spacing(1, 1.25),
    margin: 0,
  },
}));

const ContentContainer = styled(Stack)(({ theme }) => ({
  minWidth: 1,
  marginBottom: theme.spacing(10),
  [theme.breakpoints.up("tablet")]: {
    marginBottom: 0,
  },
  [theme.breakpoints.up("desktop-s")]: {
    minWidth: "unset",
    marginBottom: 0,
    "&:first-child": {
      width: "60%",
    },
    "&:last-child": {
      flex: 1,
    },
  },
}));
